import React, { useEffect, useMemo, useRef, useState } from "react";

type AnimatedNumberProps = {
  value: number;
  /**
   * Duração da animação (ms).
   * Valores entre 700–1200ms tendem a parecer “premium” sem distrair.
   */
  durationMs?: number;
  /** Valor inicial (se omitido, usa o valor anterior exibido, ou 0). */
  from?: number;
  /** Função de formatação do número exibido. */
  format?: (n: number) => string;
  /** Desabilita animação (ex.: skeleton/SSR/otimização). */
  disabled?: boolean;
  className?: string;
  /** Para acessibilidade, descreve o valor (ex.: "Receita total"). */
  ariaLabel?: string;
};

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Renderiza um número com animação incremental até o valor final.
 *
 * SRP: este componente só anima a transição numérica; não decide tamanho de fonte.
 */
export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  durationMs = 900,
  from,
  format,
  disabled,
  className,
  ariaLabel,
}) => {
  const safeValue = Number.isFinite(value) ? value : 0;
  const initialFrom = Number.isFinite(from as unknown as number)
    ? Number(from)
    : 0;
  const [displayValue, setDisplayValue] = useState<number>(initialFrom);
  const rafRef = useRef<number | null>(null);
  const prevValueRef = useRef<number>(initialFrom);

  useEffect(() => {
    const reduce = prefersReducedMotion();
    const isDisabled = Boolean(disabled) || reduce || durationMs <= 0;

    const startValue = Number.isFinite(from as unknown as number)
      ? Number(from)
      : prevValueRef.current;
    const endValue = safeValue;

    // Se não há mudança relevante, evita animação.
    if (Object.is(startValue, endValue)) {
      prevValueRef.current = endValue;
      setDisplayValue(endValue);
      return;
    }

    if (isDisabled) {
      prevValueRef.current = endValue;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayValue(endValue);
      return;
    }

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const startTs = performance.now();
    const delta = endValue - startValue;

    const step = (now: number) => {
      const t = Math.min(1, (now - startTs) / durationMs);
      const eased = easeOutCubic(t);
      const next = startValue + delta * eased;
      setDisplayValue(next);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        prevValueRef.current = endValue;
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [safeValue, durationMs, disabled, from]);

  // Mantém a formatação estável (tabular-nums ajuda visualmente).
  const text = useMemo(() => {
    if (typeof format === "function") return format(displayValue);
    return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(
      displayValue,
    );
  }, [displayValue, format]);

  return (
    <span className={className} aria-label={ariaLabel}>
      {text}
    </span>
  );
};
